import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    BookOpen,
    ArrowRight,
    HeartHandshake,
    X,
    Mail,
    Linkedin,
    Briefcase,
    Globe,
    Award
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { THERAPISTS, Therapist } from '../components/data/services';
import { Helmet } from 'react-helmet';
import { Share2 } from 'lucide-react';
import TherapistFilter from '../components/utilities/TherapistFilters';
import ExperienceDisplay from '../components/utilities/ExperienceDisplay';


// add this function to handle a unqiue URL for each therapist
const generateTherapistShareUrl = (therapistId: string) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/therapist/${therapistId}`
}


const handleShare = (therapistId: string, therapistName: string) => {
    const shareUrl = generateTherapistShareUrl(therapistId);

    if (navigator.share) {
        navigator.share({
            title: `${therapistName} - Greh Pravesh Therapy`,
            text: `Check out ${therapistName}'s profile at Greh Pravesh Therapy`,
            url: shareUrl,
        })
            .catch(error => console.log('Error sharing:', error));
    } else {
        // Fallback - copy to clipboard
        navigator.clipboard.writeText(shareUrl)
            .then(() => {
                // Show a toast or alert that the link was copied
                alert(`Link copied to clipboard!`);
            })
            .catch(err => {
                console.error('Failed to copy: ', err);
            });
    }
};

export const AnimatedLetter = ({ letter }: { letter: string }) => {
    return (
        <motion.span
            className="inline-block"
            variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
            }}
            transition={{ duration: 0.5 }}
        >
            {letter}
        </motion.span>
    )
}

export const AnimatedWord = ({ word }: { word: string }) => {
    return (
        <motion.span
            className="inline-block"
            variants={{
                hidden: {},
                visible: {
                    transition: {
                        staggerChildren: 0.05
                    }
                }
            }}
        >
            {word.split('').map((letter, index) => (
                <AnimatedLetter key={index} letter={letter} />
            ))}
        </motion.span>
    );
};

interface TeamMember {
    id: number;
    name: string;
    avatar: string;
    bio: string;
    specialties: string[];
    therapyTypes: ('Individual' | 'Couple' | 'Group')[];
}


// const teamMembers = Object.values(THERAPISTS).map(therapist => ({
//     id: therapist.id,
//     name: therapist.name,
//     pronouns: therapist.pronouns,
//     avatar: therapist.avatar,
//     bio: therapist.bio,
//     specialization: therapist.specialization,
//     languages: therapist.languages,
//     experience: therapist.experience,
//     contact: therapist.contact,
//     therapyTypes: therapist.serviceIds.map(id =>
//         id === 'individual-therapy' ? 'Individual' :
//             id === 'couples-therapy' ? 'Couple' : id
//     )
// }));



const TherapistModal: React.FC<{
    therapist: typeof THERAPISTS[string],
    onClose: () => void
}> = ({ therapist, onClose }) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[999] flex items-center justify-center p-4 overflow-y-auto">
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto relative"
            >
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleShare(therapist.id, therapist.name);
                    }}
                    className="absolute top-4 right-14 z-10 bg-blue-500 text-white rounded-full p-2 hover:bg-blue-600 transition-colors"
                >
                    <Share2 size={24} color="white" />
                </button>
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 bg-gray-100 rounded-full p-2 hover:bg-gray-200 transition-colors"
                >
                    <X size={24} />
                </button>

                {/* Header with Avatar */}
                <div className="bg-gradient-to-br from-teal-500 to-teal-700 rounded-t-xl p-6 flex items-center">
                    <img
                        src={therapist.avatar}
                        alt={therapist.name}
                        className="w-24 h-24 rounded-full object-cover mr-6 border-4 border-white"
                    />
                    <div>
                        <h2 className="text-3xl font-bold text-white">{therapist.name}</h2>
                        <p className="text-white/80">{therapist.pronouns}</p>
                    </div>
                </div>

                {/* Detailed Information Container */}
                <div className="p-6 grid md:grid-cols-3 gap-6">
                    {/* Left Column: Contact & Basic Info */}
                    <div className="md:col-span-1 space-y-6">
                        <div>
                            <h3 className="text-xl font-semibold mb-3 text-gray-800 flex items-center">
                                <Mail className="mr-2 text-teal-600" size={20} />
                                Contact
                            </h3>
                            <div className="space-y-2">
                                <a
                                    href={`mailto:${therapist.contact.email}`}
                                    className="block hover:text-teal-700 truncate max-w-[200px]"
                                >
                                    {therapist.contact.email}
                                </a>
                                {therapist.contact.phone && (
                                    <p>{therapist.contact.phone}</p>
                                )}
                                <a
                                    href={therapist.contact.linkedin}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center hover:text-blue-700"
                                >
                                    <Linkedin className="mr-2" size={20} />
                                    LinkedIn Profile
                                </a>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-xl font-semibold mb-3 text-gray-800 flex items-center">
                                <Briefcase className="mr-2 text-orange-600" size={20} />
                                Experience
                            </h3>
                            <p>{therapist.experience} Years</p>
                        </div>
                    </div>

                    {/* Middle Column: Qualifications & Languages */}
                    <div className="md:col-span-1 space-y-6">
                        <div>
                            <h3 className="text-xl font-semibold mb-3 text-gray-800 flex items-center">
                                <Award className="mr-2 text-purple-600" size={20} />
                                Qualifications
                            </h3>
                            {therapist.qualifications.map((qual, index) => (
                                <div key={index} className="mb-2">
                                    <p className="font-medium">{qual.degree}</p>
                                    {qual.institution && <p className="text-gray-600 text-sm">{qual.institution}</p>}
                                </div>
                            ))}
                        </div>

                        <div>
                            <h3 className="text-xl font-semibold mb-3 text-gray-800 flex items-center">
                                <Globe className="mr-2 text-green-600" size={20} />
                                Languages
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {therapist.languages.map(lang => (
                                    <span
                                        key={lang}
                                        className="bg-green-50 text-green-800 text-xs px-2 py-1 rounded-full"
                                    >
                                        {lang}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Detailed Info */}
                    <div className="md:col-span-1 space-y-6">
                        <div>
                            <h3 className="text-xl font-semibold mb-3 text-gray-800 flex items-center">
                                <BookOpen className="mr-2 text-blue-600" size={20} />
                                Specializations
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {therapist.specialization.map(spec => (
                                    <span
                                        key={spec}
                                        className="bg-blue-50 text-blue-800 text-xs px-2 py-1 rounded-full"
                                    >
                                        {spec}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Full Width: Detailed Bio */}
                    <div className="md:col-span-3">
                        <h3 className="text-xl font-semibold mb-3 text-gray-800">About Me</h3>
                        <p className="text-gray-700">{therapist.bio}</p>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};


const OurTeamPage: React.FC = () => {
    const [selectedTherapist, setSelectedTherapist] = useState<typeof THERAPISTS[string] | null>(null);


    const [filteredTherapists, setFilteredTherapists] = useState<Therapist[]>(Object.values(THERAPISTS));


    const navigate = useNavigate();

    // eslint-disable-next-line
    const handleBookAppointment = (member: TeamMember) => {
        console.log(`Booking appointment with ${member.name}`);
    };

    // Handler for filter changes
    const handleFilterChange = (filtered: Therapist[]) => {
        setFilteredTherapists(filtered);
    };

    return (
        <>
            <Helmet>
                {/* Basic Meta Tags */}
                <title>Meet Our Mental Health Experts & Counselors | Greh Pravesh Therapy</title>
                <meta name="description" content="Experienced therapists for anxiety, depression, and relationship challenges. Personalized care for your mental health needs. Call 8591049845." />
                {/* Open Graph Tags */}
                <meta property="og:title" content="Meet Our Mental Health Experts & Counselors | Greh Pravesh Therapy" />
                <meta property="og:description" content="Find the right counselor for your emotional wellbeing. Our experts help with stress, relationship issues, personal problems, and mental health support. Easy online booking available." />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="/images/team-1.png" />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Mental Health Experts & Life Coaches | Greh Pravesh Therapy" />
                <meta name="twitter:description" content="Get help from experienced counselors for stress, relationships, personal growth & emotional support. Easy online booking for in-person or virtual sessions." />

                {/* Keywords */}
                <meta name="keywords" content="mental health expert, counselor near me, relationship counseling, marriage counselor, stress management, anxiety help, depression counseling, personal development coach, online therapy, emotional wellness, mind doctor, mental health support, psychological help" />

                {/* Schema.org JSON-LD */}
                <script type="application/ld+json">
                    {`
          {
            "@context": "https://schema.org",
            "@type": "MedicalClinic",
            "@id": "https://grehpraveshtherapy.com",
            "name": "Greh Pravesh Therapy",
            "description": "Professional mental health and counseling services for individuals, couples, and families. Expert help for emotional wellness, relationships, and personal growth.",
            "medicalSpecialty": [
              "Mental Health Services",
              "Counseling Psychology",
              "Marriage & Family Therapy"
            ],
            "availableService": [
              {
                "@type": "MedicalTherapy",
                "name": "Individual Counseling",
                "description": "One-on-one therapy sessions for personal growth and mental wellness"
              },
              {
                "@type": "MedicalTherapy",
                "name": "Couples Therapy",
                "description": "Relationship counseling for couples facing challenges"
              },
              {
                "@type": "MedicalTherapy",
                "name": "Group Therapy",
                "description": "Supportive group sessions for shared healing and growth"
              }
            ],
            "staff": {
              "@type": "MedicalOrganization",
              "name": "Greh Pravesh Therapy Team",
              "description": "Experienced mental health professionals and counselors"
            },
            "openingHours": "Mo-Sa 09:00-20:00",
            "isAcceptingNewPatients": true
          }
        `}
                </script>
            </Helmet>

            <main className="flex-grow">
                {/* Landing Section with Background */}
                {/* <div
                className="flex flex-col h-[120vh] md:flex-row md:min-h-screen relative bg-cover bg-center"
                style={{
                    backgroundImage: `url('/images/team-bg.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            > */}
                <div className='flex flex-col h-[120vh] md:flex-row relative bg-orange-400 overflow-hidden bg-gradient-to-br from-orange-400 to-orange-300'>
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-teal-800 to-teal-300 rounded-br-[100%] z-0"></div>
                    <div className="absolute top-0 right-0 w-full h-full bg-contain bg-no-repeat bg-right-top opacity-10 md:opacity-20 z-0"></div>

                    <motion.section
                        className="w-full md:w-1/2 flex items-center justify-center px-6 py-12 mt-20 md:mt-0 md:py-24 relative z-10"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="max-w-xl w-full md:ml-5 lg:ml-12 text-center md:text-left">
                            <h1 className="font-fraunces-slab text-3xl md:text-6xl lg:text-7xl leading-tight mb-6 text-white md:text-zinc-900">
                                <span className="block text-2xl md:text-3xl mb-4 font-gentium-book-plus-regular text-white">
                                    Greh Pravesh Therapy
                                </span>
                                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                                    <motion.span
                                        initial="hidden"
                                        animate="visible"
                                        variants={{
                                            hidden: {},
                                            visible: {
                                                transition: {
                                                    staggerChildren: 0.8
                                                }
                                            }
                                        }}
                                        className="font-gentium-book-plus-regular text-white"
                                    >
                                        <AnimatedWord word="Meet " />
                                    </motion.span>
                                    <motion.span
                                        initial="hidden"
                                        animate="visible"
                                        variants={{
                                            hidden: {},
                                            visible: {
                                                transition: {
                                                    staggerChildren: 0.8
                                                }
                                            }
                                        }}
                                        className="font-gentium-book-plus-regular text-white"
                                    >
                                        <AnimatedWord word="Our " />
                                    </motion.span>
                                    <motion.span
                                        initial="hidden"
                                        animate="visible"
                                        variants={{
                                            hidden: {},
                                            visible: {
                                                transition: {
                                                    staggerChildren: 0.8
                                                }
                                            }
                                        }}
                                        className="font-gentium-book-plus-regular text-white"
                                    >
                                        <AnimatedWord word="Team " />
                                    </motion.span>
                                    <motion.span
                                        initial="hidden"
                                        animate="visible"
                                        variants={{
                                            hidden: {},
                                            visible: {
                                                transition: {
                                                    staggerChildren: 0.8
                                                }
                                            }
                                        }}
                                        className="font-gentium-book-plus-regular text-white"
                                    >
                                        <AnimatedWord word="of " />
                                    </motion.span>
                                    <motion.span
                                        initial="hidden"
                                        animate="visible"
                                        variants={{
                                            hidden: {},
                                            visible: {
                                                transition: {
                                                    staggerChildren: 0.8
                                                }
                                            }
                                        }}
                                        className="font-gentium-book-plus-regular text-white"
                                    >
                                        <AnimatedWord word="Experts" />
                                    </motion.span>
                                </div>

                                <p className="text-base md:text-xl mt-6 text-softwhite">
                                    Expert therapists dedicated to your emotional well-being,
                                    providing personalized support for individuals, couples,
                                    and families.
                                </p>
                            </h1>
                        </div>
                    </motion.section>

                    <motion.section
                        className="w-full md:w-1/2 h-screen relative"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.9, delay: 0.3 }}
                    >
                        <img
                            src="/images/team-1.png"
                            alt="our team"
                            className="w-full h-full object-cover"
                        />
                    </motion.section>

                </div>

                {/* Team Section */}
                <section className="bg-gray-50 py-16 px-4">
                    <div className="container mx-auto">
                        <h2 className="text-4xl font-gentium-book-plus-regular font-bold text-center mb-12 text-gray-800">
                            Meet Our Therapy Experts
                        </h2>

                        <TherapistFilter
                            therapists={Object.values(THERAPISTS)}
                            onFilterChange={handleFilterChange}
                        />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {/* Use filteredTherapists instead of teamMembers */}
                            {filteredTherapists.map((therapist) => {
                                const member = {
                                    id: therapist.id,
                                    name: therapist.name,
                                    pronouns: therapist.pronouns,
                                    avatar: therapist.avatar,
                                    bio: therapist.bio,
                                    specialization: therapist.specialization,
                                    languages: therapist.languages,
                                    experience: therapist.experience,
                                    contact: therapist.contact,
                                    therapyTypes: therapist.serviceIds.map(id =>
                                        id === 'individual-therapy' ? 'Individual' :
                                            id === 'couples-therapy' ? 'Couple' : id
                                    )
                                };
                                return (
                                    <motion.div
                                        key={member.id}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5 }}
                                        whileHover={{ scale: 1.05 }}
                                        className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-2xl"
                                    >
                                        {/* Rest of the therapist card remains the same... */}
                                        <div className="relative flex justify-center items-center p-4 bg-gray-100">
                                            <img
                                                src={member.avatar}
                                                alt={member.name}
                                                loading='lazy'
                                                className="w-48 h-48 object-cover rounded-full border-4 border-white shadow-lg"
                                            />
                                        </div>

                                        <div className="p-6 pt-4">
                                            <h3 className="text-2xl font-semibold mb-2 text-gray-800 text-center">
                                                {member.name}
                                            </h3>
                                            <p className="text-center text-gray-600 mb-4">
                                                {member.pronouns}
                                            </p>

                                            <div className="flex justify-center space-x-4 mb-4">
                                                <a
                                                    href={member.contact.linkedin}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800"
                                                >
                                                    <Linkedin size={24} />
                                                </a>
                                                <a
                                                    href={`mailto:${member.contact.email}`}
                                                    className="text-teal-600 hover:text-teal-800"
                                                >
                                                    <Mail size={24} />
                                                </a>
                                            </div>

                                            <div className="text-center mb-4">
                                                <span className="bg-orange-50 text-orange-800 text-xs px-2 py-1 rounded-full">
                                                    <ExperienceDisplay years={member.experience} />
                                                </span>
                                            </div>

                                            <div className="flex justify-center space-x-2 mb-4">
                                                {member.specialization.slice(0, 2).map(spec => (
                                                    <span
                                                        key={spec}
                                                        className="bg-blue-50 text-blue-800 text-xs px-2 py-1 rounded-full"
                                                    >
                                                        {spec}
                                                    </span>
                                                ))}
                                            </div>

                                            <div className="flex space-x-2 justify-center">
                                                <button
                                                    onClick={() => navigate('/book-appointment')}
                                                    className="bg-teal-500 text-white text-sm md:text-md px-4 py-2 rounded-lg hover:bg-teal-600 transition-colors"
                                                >
                                                    Book Appointment
                                                </button>
                                                <button
                                                    onClick={() => navigate(`/therapist/${member.id}`)}
                                                    className="bg-orange-500 text-white text-sm md:text-md  px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
                                                >
                                                    Read More
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleShare(member.id, member.name);
                                                    }}
                                                    className="bg-blue-500 text-white text-sm md:text-md px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                                                >
                                                    <Share2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Therapist Modal */}
                {selectedTherapist && (
                    <TherapistModal
                        therapist={selectedTherapist}
                        onClose={() => setSelectedTherapist(null)}
                    />
                )}

                <section className="bg-white py-16 px-4">
                    <div className="container mx-auto max-w-4xl">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6 }}
                            className="bg-purple rounded-xl shadow-2xl overflow-hidden relative"
                        >
                            {/* Background Graphics */}
                            <div className="absolute top-0 left-0 w-full h-full opacity-10">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 800 400"
                                    className="w-full h-full"
                                >
                                    <circle cx="200" cy="100" r="150" fill="white" />
                                    <circle cx="600" cy="300" r="100" fill="white" />
                                </svg>
                            </div>

                            <div className="p-8 md:p-12 text-center relative z-10">
                                <div className="flex flex-col md:flex-row items-center justify-center">
                                    <div className="md:mr-8 mb-6 md:mb-0">
                                        <HeartHandshake
                                            className="mx-auto text-white"
                                            size={80}
                                            strokeWidth={1.5}
                                        />
                                    </div>
                                    <div className="flex-grow">
                                        <h2 className="text-3xl md:text-4xl font-gentium-book-plus-regular font-bold text-white mb-4">
                                            Join Our Team
                                        </h2>
                                        <p className="text-base md:text-xl text-white/90 mb-8">
                                            Are you a passionate therapist looking to make a difference?
                                            We're always seeking compassionate professionals to join our team.
                                        </p>
                                        <button
                                            onClick={() => navigate("/join-us")}
                                            className="bg-white text-purple-600 font-gentium-book-plus-regular px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center mx-auto"
                                        >
                                            Work with Us
                                            <ArrowRight className="ml-2 text-purple-600" size={20} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

            </main>
        </>
    )
}

export default OurTeamPage;